import { Formik } from 'formik';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FieldCard, FormField, Skeleton } from '@components';
import { getCacheFileExtensionFormConfig, getCacheFormConfig, SCREEN_BREAKPOINTS } from '@constants';
import {
  ApiEndpoint,
  cacheLevelType,
  cacheTTLOptionType,
  FormActionType,
  FormType,
  InputType,
  ServiceModalName,
  StatusType,
  Translation,
} from '@enums';
import {
  useAppDispatch,
  useCreateModal,
  useErrorFormInner,
  useFormContent,
  useFormError,
  useFormLoadingStatus,
  useFormSaveStatus,
  useFormSubmissionStatus,
  useMediaQuery,
} from '@hooks';
import { addServiceModal, addSnack, submitFormRequest, updateFormProcedure } from '@store';
import type { CacheFormProps, FormConfig, FormFieldProps, PredefinedCacheViewProps } from '@types';
import { getSeconds, getValueAndUnit, validateValueEqual } from '@utils';

export const PredefinedCacheView: FC<PredefinedCacheViewProps> = ({ formikRef, onFormikInstance }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnCache;

  const { t: tToasts } = useTranslation(Translation.Configuration, { keyPrefix: 'toasts.predefinedCache' });
  const { t: tForm } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.cache' });
  const { t: tCacheModal } = useTranslation(Translation.Configuration, { keyPrefix: 'modals.cache' });
  const { t: tCommon } = useTranslation(Translation.Common);

  const formList = useFormContent({ formType });
  const isSubmitting = useFormSubmissionStatus({ formType });
  const formLoading = useFormLoadingStatus({ formType });
  const saveSuccess = useFormSaveStatus(formType);
  const submitError = useFormError({ formType });
  const clearFormSubmitting = useFormSubmissionStatus({ formType, endpoint: ApiEndpoint.CLEAR });
  const fullFormSubmitting = useFormSubmissionStatus({ formType });
  const fullFormLoading = useFormLoadingStatus({ formType, endpoint: ApiEndpoint.CLEAR });

  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const [showCurrentToast, setShowCurrentToast] = useState<boolean>(false);
  const [currentDataKey, setCurrentDataKey] = useState<string | null>(null);

  const predefinedCacheInitialValues: CacheFormProps = {
    status: true,
    cache_mode: cacheLevelType.Standard,
    server_cache_ttl: cacheTTLOptionType.Follow_Origin_Cache_Header,
    browser_cache_ttl: cacheTTLOptionType.Follow_Origin_Cache_Header,
    overwrite_cache_header: '0',
    extensions: [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'tiff',
      'webp',
      'ico',
      'svg',
      'js',
      'css',
      'woff',
      'woff2',
      'ttf',
      'tif',
      'tiff',
    ],
  };

  const initialValues: CacheFormProps = useMemo(
    () => ({
      status: formList?.status ?? predefinedCacheInitialValues.status,
      cache_mode: formList?.cache_mode ?? predefinedCacheInitialValues.cache_mode,
      server_cache_ttl:
        getSeconds(formList?.server_cache_ttl, formList?.server_cache_ttl_unit) ??
        predefinedCacheInitialValues.server_cache_ttl,
      browser_cache_ttl:
        getSeconds(formList?.browser_cache_ttl, formList?.browser_cache_ttl_unit) ??
        predefinedCacheInitialValues.browser_cache_ttl,
      overwrite_cache_header: formList?.overwrite_cache_header ?? predefinedCacheInitialValues.overwrite_cache_header,
      extensions: formList?.extensions ?? predefinedCacheInitialValues.extensions,
    }),
    [formList],
  );

  const handleSubmit = useCallback(
    (values: CacheFormProps) => {
      const {
        extensions: oldExtensions,
        browser_cache_ttl: browser_ttl_value,
        server_cache_ttl: server_ttl_value,
        ...updatedValues
      } = values;
      const extensions = oldExtensions?.map((value) => value.replace(/\n/g, '')) ?? [];
      const { value: browser_cache_ttl, unit: browser_cache_ttl_unit } = getValueAndUnit(Number(browser_ttl_value));
      const { value: server_cache_ttl, unit: server_cache_ttl_unit } = getValueAndUnit(Number(server_ttl_value));

      dispatch(
        submitFormRequest({
          formType,
          formData: {
            application_id,
            extensions,
            browser_cache_ttl,
            browser_cache_ttl_unit,
            server_cache_ttl,
            server_cache_ttl_unit,
            ...updatedValues,
          },
          globalOptions: {
            returnResult: true,
            content: true,
          },
        }),
      );
    },
    [dispatch, application_id, formType],
  );

  const handlePurgeCache = () => {
    dispatch(updateFormProcedure({ formType, procedure: FormActionType.Purge }));
    dispatch(
      addServiceModal({
        name: ServiceModalName.ConfirmModal,
        payload: {
          label: {
            title: tCacheModal('purgeEverything'),
            text: tCacheModal('purgeDescription'),
            confirmButton: tCommon('button.confirm'),
          },
          option: {
            modalType: StatusType.Warning,
            modalTable: {
              formType,
              endpoint: ApiEndpoint.CLEAR,
            },
            modalForm: {
              preventDefault: {
                callback: true,
              },
            },
            button: {
              loading: clearFormSubmitting,
            },
          },
          callbackFunction: {
            submit: () =>
              dispatch(
                submitFormRequest({
                  formType,
                  params: {
                    application_id: application_id,
                  },
                  globalOptions: {
                    endpoint: ApiEndpoint.CLEAR,
                  },
                }),
              ),
          },
        },
      }),
    );
  };

  const OpenExtensionsModal = (config: FormConfig) => {
    const { openModal } = useCreateModal({
      formType,
      initialValues,
      formConfig: config,
      handleSubmit,
    });

    return openModal;
  };

  const renderFormField = (field: FormFieldProps, formikValues: CacheFormProps) => {
    let isDisabled = isSubmitting;
    if (field.inactive) {
      const { field: watchField, condition } = field.inactive;
      const watchedValue = formikValues?.[watchField];
      isDisabled = condition(watchedValue) || isSubmitting;
    }
    const isSwitch = validateValueEqual(field.inputType, InputType.Switch);

    const { name, label, description, ...props } = field;

    return (
      <FieldCard key={name} title={field.title ?? label} description={description} besideTitle={isSwitch && !isAboveSm}>
        <FormField name={name} label={label} key={name} disabled={isDisabled} {...props} />
      </FieldCard>
    );
  };

  useEffect(() => {
    if (!submitError && !isSubmitting && saveSuccess) {
      if (showCurrentToast && currentDataKey) {
        dispatch(addSnack({ type: StatusType.Success, message: tToasts(`${currentDataKey}Configured`) }));
        setShowCurrentToast(false);
        setCurrentDataKey(null);
      }
    }
  }, [application_id, currentDataKey, isSubmitting, saveSuccess, showCurrentToast, submitError]);

  useEffect(() => {
    if (formikRef.current?.errors && Object.keys(formikRef.current.errors).length > 0) {
      formikRef.current.validateForm();
    }
  }, [tForm]);

  if (formLoading) {
    return (
      <div className="space-y-4">
        {[...Array(7)].map((_, index) => (
          <Skeleton key={index} className="flex w-full rounded-2xl h-32" />
        ))}
      </div>
    );
  }
  const SecurityFormInner = () => {
    useErrorFormInner({ formType, isSubmitting });
    return null;
  };

  return (
    <Formik<CacheFormProps> innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);

        const extensionConfig = getCacheFileExtensionFormConfig({ value: formik.values.extensions });
        const openExtensionsModal = OpenExtensionsModal(extensionConfig);

        const predefinedCacheFields = getCacheFormConfig({
          loading: isSubmitting,
          openExtensionsModal,
          handlePurgeCache,
          purgeDisabled: fullFormLoading || fullFormSubmitting,
          statusValue: formik.values.status,
        }).inputList;

        return (
          <Fragment>
            <SecurityFormInner />
            {predefinedCacheFields.map((field, index) => renderFormField(field, formik.values))}
          </Fragment>
        );
      }}
    </Formik>
  );
};
